import { arrivalStop, collectionsForInjection } from "@/lib/turnaroundCollections"
import { reportedRouteVolume } from "@/lib/volumeReporting"
import { RouteInstance, TurnaroundRoute } from "@/types/collections"

/** An ETA this close to `now`, and not yet past, puts a route on final approach. */
const FINAL_APPROACH_MINUTES = 15

/**
 * The day's route instances as rows on the Arrivals Board.
 *
 * Only routes that arrive at a depot -- a depot injection, or a shipper's direct injection --
 * and are not cancelled. `now` matters only for the "final approach" status: a route in progress,
 * not yet at the depot, whose ETA is within the next fifteen minutes.
 */
export function turnaroundsFrom(
  routeInstances: RouteInstance[] | undefined,
  now: Date,
): TurnaroundRoute[] {
  if (!routeInstances || routeInstances.length === 0) {
    return []
  }

  const routesArriving = routeInstances.filter((ri: RouteInstance) => {
    if (ri.status === "cancelled") return false
    return !!arrivalStop(ri.stops ?? [])
  })

  return routesArriving.map((ri: RouteInstance): TurnaroundRoute => {
    const depotStop = arrivalStop(ri.stops ?? [])

    const depotStopIndex = depotStop ? ri.stops?.findIndex((s) => s.id === depotStop.id) : -1

    /**
     * What is on the van for this injection, shipper by shipper.
     *
     * The collections since the previous unload, so a run that drops at a spoke and carries on
     * collecting does not announce the spoke's cages twice -- see lib/turnaroundCollections.
     * `customerNames` is derived from the same list rather than gathered separately, so the
     * desktop column, the CSV and the card cannot come to list different shippers.
     */
    const collections = depotStop ? collectionsForInjection(ri.stops ?? [], depotStop.id) : []
    const customerNames = collections.map((collection) => collection.name)

    const nextStopAfterDepot =
      depotStopIndex >= 0 && depotStopIndex < (ri.stops?.length || 0) - 1
        ? ri.stops[depotStopIndex + 1]
        : null

    let turnaroundStatus: TurnaroundRoute["status"] = "en_route"
    if (ri.status === "completed") turnaroundStatus = "completed"
    else if (!ri.actual_start_time) {
      turnaroundStatus = "not_started"
    } else if (ri.status === "in_progress") {
      if (depotStop?.actual_arrival) {
        turnaroundStatus = "processing"
      } else {
        const etaTime = depotStop?.estimated_arrival ? new Date(depotStop.estimated_arrival) : null

        if (etaTime) {
          const minutesUntilArrival = (etaTime.getTime() - now.getTime()) / (1000 * 60)
          if (minutesUntilArrival <= FINAL_APPROACH_MINUTES && minutesUntilArrival >= 0) {
            turnaroundStatus = "final_approach"
          } else {
            turnaroundStatus = "en_route"
          }
        } else {
          turnaroundStatus = "en_route"
        }
      }
    }

    // A turnaround is a stop after the depot: the van goes back out.
    const hasTurnaround = !!nextStopAfterDepot
    const nextDeparture =
      hasTurnaround && depotStop
        ? depotStop.actual_departure
          ? new Date(depotStop.actual_departure)
          : depotStop.planned_departure
            ? new Date(depotStop.planned_departure)
            : undefined
        : undefined
    const nextDestination = nextStopAfterDepot?.name || undefined

    // The depot stop's site name, falling back to the stop name and then the route's depot.
    // A direct injection's location is the shipper's, so its depot is the route's.
    const depotLocation = depotStop?.location
    const depotName =
      depotStop?.stop_type === "direct_injection"
        ? ri.depot
        : depotLocation?.site_name || depotStop?.name || ri.depot

    /**
     * The volume figure this board promises the depot.
     *
     * Confirmed counts where the ops team has entered them in the stop sheet, forecast for the
     * stops nobody has counted yet — the shared rule, so the number here and the number on the
     * stop sheet cannot disagree. A part-counted route keeps the forecast for its uncounted stops,
     * and a confirmed empty collection counts as zero.
     */
    const volume = reportedRouteVolume(
      (ri.stops || []).map((s) => ({
        volumeForecast: s.volume_forecast,
        volumeActual: s.volume_actual,
        loadingUnitsForecast: s.loading_units_forecast,
        loadingUnitsActual: s.loading_units_actual,
        loadingUnitType: s.loading_unit_type,
      })),
    )

    return {
      id: ri.id,
      vehicleReg: ri.vehicle_reg || "Unknown",
      vehicleType: ri.vehicle_type,
      driverName: ri.driver_name || "Unknown",
      routeCode: ri.code,
      samsaraRouteId: ri.samsara_route_id,
      plannedArrival: new Date(depotStop?.planned_arrival || ri.planned_end_time),
      estimatedArrival: depotStop?.estimated_arrival
        ? new Date(depotStop.estimated_arrival)
        : undefined,
      actualArrival: depotStop?.actual_arrival ? new Date(depotStop.actual_arrival) : undefined,
      arrivalDelayMinutes: depotStop?.delay_minutes || ri.delay_minutes,
      status: turnaroundStatus,
      volumeEstimated: volume.forecastVolume,
      volumeActual:
        volume.source === "forecast"
          ? undefined
          : (ri.stops || []).reduce((acc, s) => acc + (s.volume_actual ?? 0), 0),
      volumeReported: volume.volume,
      volumeSource: volume.source,
      stopsConfirmed: volume.stopsConfirmed,
      stopsCarryingVolume: volume.stopsCarryingVolume,
      loadingUnitsReported: volume.loadingUnits,
      loadingUnitType: volume.unitType,
      hasTurnaround: hasTurnaround,
      nextDeparture: nextDeparture,
      nextDestination: nextDestination,
      terminatesHere: !hasTurnaround,
      depot: depotName,
      collections,
      customerNames,
    }
  })
}
